import {
  PageBlueprint,
  createFrontendPlugin,
  createRouteRef,
} from '@backstage/frontend-plugin-api';
import MenuBookIcon from '@material-ui/icons/MenuBook';

/**
 * Shared route ref so the page and any deep-linkers point at the same
 * destination. Required by `PageBlueprint` for the framework to
 * auto-discover a sidebar nav item from this page.
 */
const kindsGuideRouteRef = createRouteRef();

/**
 * Routable page for the catalog kinds guide. The lazy `import()` keeps the
 * page bundle out of the initial chunk — the user only downloads it when
 * they navigate to `/kinds-guide`.
 *
 * NOTE: a sidebar entry is created automatically by the framework from
 * this page's `title` + `icon` + `routeRef` (the old `NavItemBlueprint`
 * approach is deprecated). Our `Sidebar.tsx` already calls `nav.rest()`
 * so the item just appears.
 */
const kindsGuidePage = PageBlueprint.make({
  // No `name` here on purpose — the resulting extension id becomes
  // `page:kinds-guide`, matching the convention used by `page:home`,
  // `page:catalog`, etc., so we can pluck it out of the sidebar with a
  // simple `nav.take('page:kinds-guide')`.
  params: {
    path: '/kinds-guide',
    title: 'Kinds Guide',
    icon: <MenuBookIcon />,
    routeRef: kindsGuideRouteRef,
    // `noHeader: true` because KindsGuidePage already renders its own
    // `<Header>` (the blue banner). Without this, our custom
    // `BoschPageLayout` would stack a second blue banner on top of it.
    // See `packages/app/src/modules/theme/pageLayout.tsx`.
    noHeader: true,
    loader: () =>
      import('../../components/kinds/KindsGuidePage').then(m => (
        <m.KindsGuidePage />
      )),
  },
});

/**
 * Registered as a frontend PLUGIN (not a module) because nav-item
 * auto-discovery from `PageBlueprint` only fires reliably when the page
 * lives inside a `createFrontendPlugin`. The bundled plugins
 * (`@backstage/plugin-catalog/alpha`, etc.) all follow this pattern.
 */
export const kindsModule = createFrontendPlugin({
  pluginId: 'kinds-guide',
  routes: {
    kindsGuide: kindsGuideRouteRef,
  },
  extensions: [kindsGuidePage],
});
